// src/components/reports/SupervisorReport/index.tsx 
import React, { useState, useEffect, useRef } from 'react';
import { useQuery } from '@tanstack/react-query';
import {
  Box,
  Typography,
  CircularProgress,
  Alert,
  Grid,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Button,
  Tabs,
  Tab
} from '@mui/material';

import { SupervisorCustomerReportItem, FilterItem } from '../../../../types/sales';
import { salesReportService } from '../../../../services/salesReportService';
import { useAuth } from 'react-oidc-context';
import { SupervisorCustomerChart } from './SupervisorCustomerChart';
import { useReportContext } from '../../../../contexts/ReportContext';
import { ReportTabType } from '../../../../contexts/ReportContext';

import { SupervisorTabs } from './SupervisorTabs';
import { DetailedReport } from './DetailedReport';
import { SupervisorCustomerTable } from './SupervisorCustomerTable';
import { BatchPrintDialog } from './Printing/BatchPrintDialog';
import { PrintButton } from './Printing/PrintButton';

interface SupervisorCustomerReportProps {
  startMonth: string;
  endMonth: string;
}

export const SupervisorCustomerReport: React.FC<SupervisorCustomerReportProps> = ({
  startMonth,
  endMonth
}) => {
  const auth = useAuth();
  const [viewMode, setViewMode] = useState<'report' | 'chart' | 'table'>('report');

  // フィルター用のデータ
  const [supervisors, setSupervisors] = useState<FilterItem[]>([]);
  const [customers, setCustomers] = useState<FilterItem[]>([]);
  const [selectedSupervisor, setSelectedSupervisor] = useState<string>("");
  const [selectedCustomer, setSelectedCustomer] = useState<string>("");
  const [filtersLoading, setFiltersLoading] = useState(false);

  // 一括印刷用の状態
  const [openBatchPrintDialog, setOpenBatchPrintDialog] = useState(false);
  const [selectedSupervisorsForPrint, setSelectedSupervisorsForPrint] = useState<string[]>([]);
  const [isPrinting, setIsPrinting] = useState(false);

  // レポートキャプチャ用の状態
  const [capturingSupervisor, setCapturingSupervisor] = useState<string | null>(null);
  const [captureCallback, setCaptureCallback] = useState<((html: string) => void) | null>(null);
  const [isRenderingReport, setIsRenderingReport] = useState<boolean>(false);
  const captureTriggerRef = useRef<number>(0);

  // 表示中のレポート部分への参照
  const reportRef = useRef<HTMLDivElement | null>(null);

  // ReportContextから必要な関数を取得
  const {
    isCacheValid,
    getCachedData,
    cacheReportData,
    markReportAsDisplayed
  } = useReportContext();

  // 定数としてキャッシュキーを定義
  const cacheKey: ReportTabType = 'supervisor-customer';

  // フィルターデータ取得
  useEffect(() => {
    const fetchSupervisors = async () => {
      try {
        setFiltersLoading(true);
        const token = auth.user?.access_token;

        // 担当者一覧を取得
        const supervisorsData = await salesReportService.getSupervisorsList(token);
        setSupervisors(supervisorsData);
      } catch (err: any) {
        console.error('担当者データの取得に失敗しました:', err);
      } finally {
        setFiltersLoading(false);
      }
    };

    fetchSupervisors();
  }, [auth.user?.access_token]);

  // 選択された担当者でデータをフィルタリングする関数
  const getFilteredData = (): SupervisorCustomerReportItem[] => {
    const detailData = getDetailData();

    if (!selectedSupervisor) {
      return detailData; // 担当者が選択されていない場合はすべての担当者を表示
    }

    // 選択された担当者のデータのみをフィルタリング
    return detailData.filter(item => item.supervisor_name === selectedSupervisor);
  };

  // キャッシュのチェック
  const shouldFetch = !isCacheValid(cacheKey);
  const cachedItem = getCachedData(cacheKey);

  // キャッシュからフィルター値を復元
  useEffect(() => {
    if (cachedItem && !selectedSupervisor && !selectedCustomer) {
      // キャッシュからフィルター値を復元
      setSelectedSupervisor(cachedItem.filters?.supervisor || '');
      setSelectedCustomer(cachedItem.filters?.customer || '');
    }
  }, [cachedItem, selectedSupervisor, selectedCustomer]);

  // React Queryを使用してデータ取得
  const { data, isLoading, error } = useQuery({
    queryKey: ['supervisorCustomerReport', { startMonth, endMonth, selectedSupervisor, selectedCustomer }],
    queryFn: async () => {
      const token = auth.user?.access_token;
      return await salesReportService.getSupervisorCustomerReport(
        startMonth,
        endMonth,
        selectedSupervisor,
        selectedCustomer,
        token
      );
    },
    enabled: shouldFetch || !cachedItem, // キャッシュが無効な場合のみフェッチ
    staleTime: 5 * 60 * 1000, // 5分間はデータをstaleと見なさない
  });

  // データ取得成功時の処理
  useEffect(() => {
    if (data) {
      // フィルター情報を含めたデータオブジェクトをキャッシュに保存
      const cacheData = {
        reportData: data,
        filters: {
          supervisor: selectedSupervisor,
          customer: selectedCustomer
        }
      };

      // データをコンテキストのキャッシュに保存
      cacheReportData(cacheKey, cacheData);
      // レポートが表示されたことをマーク
      markReportAsDisplayed();
    }
  }, [data, cacheReportData, cacheKey, markReportAsDisplayed, selectedSupervisor, selectedCustomer]);

  // エラー発生時の処理
  useEffect(() => {
    if (error) {
      console.error('担当者×顧客別レポートの取得に失敗しました:', error);
    }
  }, [error]);

  // 表示するデータを決定（キャッシュまたは新たに取得したデータ）
  const reportData = (shouldFetch || !cachedItem) ? data : cachedItem?.reportData;

  // 合計を取得（最後の要素が合計行）
  const getTotals = (): SupervisorCustomerReportItem | null => {
    if (!reportData || reportData.length === 0) return null;
    return reportData[reportData.length - 1];
  };

  // 詳細データ（合計行を除く）
  const getDetailData = (): SupervisorCustomerReportItem[] => {
    if (!reportData || reportData.length <= 1) return [];
    return reportData.slice(0, reportData.length - 1);
  };

  // 表示モード切り替えのハンドラー
  const handleViewModeChange = (event: React.SyntheticEvent, newValue: 'report' | 'chart' | 'table') => {
    setViewMode(newValue);
  };

  // 一括印刷ダイアログを開く
  const handleOpenBatchPrintDialog = () => {
    // デフォルトでは全担当者を選択状態にする
    setSelectedSupervisorsForPrint(supervisors.map(s => s.name));
    setOpenBatchPrintDialog(true);
  };

  // 一括印刷ダイアログを閉じる
  const handleCloseBatchPrintDialog = () => {
    setOpenBatchPrintDialog(false);
  };

  // チェックボックスの状態変更ハンドラー
  const handleSupervisorCheckboxChange = (supervisorName: string) => (event: React.ChangeEvent<HTMLInputElement>) => {
    if (event.target.checked) {
      setSelectedSupervisorsForPrint(prev => [...prev, supervisorName]);
    } else {
      setSelectedSupervisorsForPrint(prev => prev.filter(name => name !== supervisorName));
    }
  };

  // 全選択/全解除ハンドラー
  const handleSelectAllSupervisors = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (event.target.checked) {
      setSelectedSupervisorsForPrint(supervisors.map(s => s.name));
    } else {
      setSelectedSupervisorsForPrint([]);
    }
  };

  // 担当者×顧客詳細フィルターのレンダリング
  const renderDetailFilters = () => {
    return (
      <Box sx={{ mb: 3, p: 2, bgcolor: 'background.paper', borderRadius: 1 }}>
        <Typography variant="subtitle2" gutterBottom>
          絞り込み
        </Typography>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} sm={8}>
            <FormControl fullWidth size="small">
              <InputLabel>担当者</InputLabel>
              <Select
                value={selectedSupervisor}
                onChange={(e) => setSelectedSupervisor(e.target.value as string)}
                label="担当者"
                disabled={filtersLoading}
              >
                <MenuItem value="">すべて</MenuItem>
                {supervisors.map((supervisor) => (
                  <MenuItem key={supervisor.name} value={supervisor.name}>
                    {supervisor.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>

          <Grid item xs={12} sm={4}>
            <Button
              variant="outlined"
              onClick={() => setSelectedSupervisor("")}
              fullWidth
              disabled={filtersLoading || !selectedSupervisor}
            >
              クリア
            </Button>
          </Grid>
        </Grid>
      </Box>
    );
  };

  // 新しい関数: 担当者ごとのレポートをキャプチャする
  const handleCaptureSupervisorReport = (supervisorName: string, callback: (html: string) => void) => {
    // 現在の表示状態を保存
    const currentSupervisor = selectedSupervisor;
    const currentViewMode = viewMode;

    console.log(`キャプチャ開始: ${supervisorName}`);

    // キャプチャ用に担当者とビューモードを設定
    setSelectedSupervisor(supervisorName);
    setViewMode('report');
    setIsRenderingReport(true);

    // キャプチャ状態をセット
    setCapturingSupervisor(supervisorName);
    setCaptureCallback(() => {
      // HTML取得後のコールバック
      return (html: string) => {
        console.log(`キャプチャ完了: ${supervisorName}`);

        // コールバック実行後、キャプチャ状態をリセット
        setCapturingSupervisor(null);
        setCaptureCallback(null);
        setIsRenderingReport(false);

        // キャプチャHTMLをコールバックに渡す
        callback(html);
      };
    });

    // レンダリングトリガーの更新 (強制的に再レンダリング)
    captureTriggerRef.current += 1;
  };

  // レポート要素のレンダリング完了をチェックする効果
  useEffect(() => {
    // レンダリング中でない場合はスキップ
    if (!isRenderingReport) return;

    // キャプチャ中で、選択中の担当者とキャプチャ対象の担当者が一致し、
    // コールバック関数が設定されている場合にキャプチャを実行
    if (
      capturingSupervisor === selectedSupervisor &&
      captureCallback &&
      viewMode === 'report'
    ) {
      // レンダリング完了を待つ - IE11対応のためにタイムアウトを長め（1秒）に設定
      const timer = setTimeout(() => {
        // レポート部分のHTMLを取得
        const reportElement = document.getElementById('printable-report-section');

        if (reportElement) {
          // HTMLをキャプチャしてコールバックに渡す
          captureCallback(reportElement.innerHTML);
        } else {
          console.error('印刷対象のレポート要素が見つかりません');
          if (captureCallback) {
            captureCallback(''); // 空のHTMLを返す
          }
        }
      }, 1000); // SVGや画像のレンダリング完了を待つため1秒の遅延

      return () => clearTimeout(timer);
    }
  }, [capturingSupervisor, selectedSupervisor, captureCallback, viewMode, isRenderingReport, captureTriggerRef.current]);

  if (isLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 5 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Alert severity="error">
        データの取得中にエラーが発生しました
      </Alert>
    );
  }

  if (!reportData || reportData.length === 0) {
    return (
      <Alert severity="info">
        該当するデータがありません。条件を変更して再度お試しください。
      </Alert>
    );
  }

  // グラフのタイトル（共通）
  const chartTitle = selectedSupervisor
    ? `${selectedSupervisor} - 顧客別売上・利益 (${startMonth})`
    : `担当者×顧客別売上・利益 (${startMonth})`;

  return (
    <Box>
      {/* 一括印刷ダイアログ */}
      <BatchPrintDialog
        open={openBatchPrintDialog}
        onClose={handleCloseBatchPrintDialog}
        supervisors={supervisors}
        selectedSupervisors={selectedSupervisorsForPrint}
        onSelectAllSupervisors={handleSelectAllSupervisors}
        onSupervisorCheckboxChange={handleSupervisorCheckboxChange}
        startMonth={startMonth}
        endMonth={endMonth}
        isPrinting={isPrinting}
        setIsPrinting={setIsPrinting}
        // 追加: レポートキャプチャ関数とデータ
        onCaptureSupervisorReport={handleCaptureSupervisorReport}
        reportData={reportData}
      />

      {/* 担当者タブ */}
      <SupervisorTabs
        supervisors={supervisors}
        selectedSupervisor={selectedSupervisor}
        onSupervisorChange={setSelectedSupervisor}
      />

      {/* 一括印刷ボタン */}
      <PrintButton
        onClick={handleOpenBatchPrintDialog}
        disabled={isLoading || filtersLoading || isPrinting}
      />

      {/* 表示モード切り替えタブ */}
      <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 2 }}>
        <Tabs value={viewMode} onChange={handleViewModeChange} aria-label="表示モード">
          <Tab label="レポート" value="report" sx={{ fontWeight: 'bold' }} />
          <Tab label="グラフ表示" value="chart" />
          <Tab label="表形式" value="table" />
        </Tabs>
      </Box>

      {/* レポート表示 */}
      {viewMode === 'report' && (
        <DetailedReport
          data={reportData}
          filteredData={getFilteredData()}
          startMonth={startMonth}
          selectedSupervisor={selectedSupervisor}
          chartTitle={chartTitle}
        // キャプチャ関連のプロパティは不要。キャプチャは親コンポーネントで行う
        />
      )}

      {/* グラフ表示 */}
      {viewMode === 'chart' && (
        <Box sx={{ mt: 2, p: 2, bgcolor: '#f5f5f5', borderRadius: 1 }}>
          <Typography variant="body2" color="textSecondary" gutterBottom>
            ※参考情報：グラフ表示
          </Typography>
          {/* {renderDetailFilters()} */}
          <SupervisorCustomerChart
            data={reportData}
            title={chartTitle}
            fullSize={true}
            selectedSupervisor={selectedSupervisor}
          />
        </Box>
      )}

      {/* 表形式表示 */}
      {viewMode === 'table' && (
        <Box sx={{ mt: 2, p: 2, bgcolor: '#f5f5f5', borderRadius: 1 }}>
          <Typography variant="body2" color="textSecondary" gutterBottom>
            ※参考情報：表形式詳細
          </Typography>
          {/* {renderDetailFilters()} */}
          <SupervisorCustomerTable
            data={reportData}
            selectedSupervisor={selectedSupervisor}  // 選択された担当者情報を渡す
          />
        </Box>
      )}
    </Box>
  );
};